import React from "react";
import { Check } from "lucide-react";
import { Text, cn } from "@musa.tech/design-system";

/** `pre` = passou pela pré-seleção; `manual` = pulou. Os fluxos atuais são todos manuais. */
export type OnDemandWizardFlow = "manual" | "pre";

export interface WizardStepperStep {
  id: string;
  label: string;
}

export interface WizardStepperProps {
  steps: WizardStepperStep[];
  currentIndex: number;
  /** Current flow — determines whether the "Pré-seleção" step was skipped (grey) or used (completed). */
  flow?: OnDemandWizardFlow;
  className?: string;
}

export const WizardStepper: React.FC<WizardStepperProps> = ({
  steps,
  currentIndex,
  flow = "manual",
  className,
}) => {
  // When Pré-seleção exists in the stepper, it's "step 0" and the rest count
  // starting at 1; without it, the first step shown is already "1".
  const hasPreSelectionStep = steps[0]?.id === "preSelection";

  return (
    <div>
      <nav
        aria-label="Etapas da solicitação"
        className={cn("flex items-start justify-center", className)}
      >
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isLast = index === steps.length - 1;
          const isPreSelectionStep = step.id === "preSelection";
          // Skipped pré-seleção (manual flow, already passed) always stays grey,
          // even "completed" by index — used (flow `pre`) follows the normal state.
          const isSkipped = isPreSelectionStep && flow !== "pre" && currentIndex > 0;
          const displayNumber = hasPreSelectionStep ? index : index + 1;

          return (
            // `contents` keeps the step and connector as direct children of the nav's flex.
            <div key={step.id} className="contents">
              <div
                className={cn(
                  "flex w-8 shrink-0 flex-col items-center gap-2 sm:w-24 md:w-[150px]",
                  (isSkipped || (!isCompleted && !isCurrent)) && "opacity-60",
                )}
              >
                <div
                  aria-current={isCurrent ? "step" : undefined}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full text-[0.8125rem] font-semibold transition-colors duration-200",
                    !isSkipped && (isCompleted || isCurrent)
                      ? "bg-primary-500 text-neutral-50"
                      : "bg-neutral-200 text-neutral-700",
                  )}
                >
                  {isCompleted && !isSkipped ? (
                    <Check className="h-[15px] w-[15px]" aria-hidden="true" />
                  ) : (
                    displayNumber
                  )}
                </div>
                <Text
                  as="span"
                  variant="label"
                  size="small"
                  className={cn(
                    "hidden text-center text-xs font-medium sm:block",
                    isCurrent ? "text-primary-700" : "text-neutral-700",
                  )}
                >
                  {step.label}
                </Text>
              </div>

              {!isLast && (
                <div
                  className={cn(
                    "mx-1.5 mt-[15px] h-0.5 flex-1 transition-colors duration-200 sm:mx-2.5",
                    isCompleted && !isSkipped ? "bg-primary-500" : "bg-border",
                  )}
                />
              )}
            </div>
          );
        })}
      </nav>

      {/* Current step label — on mobile the labels next to the circle are hidden */}
      <Text
        as="p"
        variant="label"
        size="small"
        className="mt-2 text-center text-xs font-medium text-primary-700 sm:hidden"
      >
        {steps[currentIndex]?.label}
      </Text>
    </div>
  );
};
